import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  Grid,
  FormControlLabel,
  Checkbox,
  Button,
  MenuItem,
  CircularProgress,
} from "@material-ui/core";

import { ToggleButtonGroup, ToggleButton } from "@material-ui/lab";
import UploadIcon from "@material-ui/icons/CloudUpload";
import DeleteIcon from "@material-ui/icons/Delete";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import DateRangeIcon from "@material-ui/icons/DateRange";
import ScheduleDisplay from "./ScheduleDisplay";

import FloatingPanel from "./FloatingPanel";
import LayerWithType from "./LayerWithType";
import HexFillOptions from "./HexFillOptions";
import { FilledTextField, FilledSelect } from "../../components/FormElements";
import { FleetContext, FleetSettingsContext, GeofencesContext } from "../../context";

import { useDialog } from "../../hooks/useDialog";
import Permissions from "../../permissions";
import { PermissionedDisplay } from "../../components/Layout";

import {
  GeofencesPanelPositions,
  GeofencesEditModes,
  GeofencesLayerTypes,
  GeofencesIncentiveLayers,
} from "../../constants";
import buildAssetsUrl from "../../util/strings/buildAssetsUrl";

const useStyles = makeStyles((theme) => ({
  nameInput: {
    width: "100%",
    maxWidth: 200,
    margin: 0,
    marginRight: theme.spacing(2),
  },
  switchInput: { marginRight: theme.spacing(2) },
  rightSideElement: { marginLeft: "auto" },
  buttonIcon: { marginRight: theme.spacing(1) },
  panelLine: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1.5),
  },
  panelTopBorder: {
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.background.sidebarSelected}`,
  },
  toggleButtonGroup: { marginRight: theme.spacing(3) },
  disabledButton: {
    "& > span": { opacity: 0.5 },
  },
  zoneIndex: {
    margin: `0 ${theme.spacing(0.5)}px`,
    userSelect: "none",
  },
  overflowText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  scheduleText: {
    textTransform: "capitalize",
    fontWeight: "400",
  },
}));

const EditZonePanel = ({ sidewalksMode }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();

  const [openDeleteDialog, deleteDialog] = useDialog({
    title: t("Fleet.Geofences.Dialogs.DeleteZoneTitle"),
    body: t("Fleet.Geofences.Dialogs.DeleteZoneBody"),
  });

  const { activeFleet } = useContext(FleetContext);
  const { billing } = useContext(FleetSettingsContext);

  const {
    layers,
    selectedZone,
    updateZone,
    updateZoneLayer,
    uploadZone,
    deleteZone,
    selectZone,
    updateSelectedZone,
  } = useContext(GeofencesContext);

  const { editMode } = selectedZone;

  const layer = layers[selectedZone.parent];
  const layerZones = layer.zones;
  const zoneKeys = Object.keys(layerZones);
  const zoneIndex = zoneKeys.findIndex((key) => key === selectedZone.key);
  const zone = layerZones[selectedZone.key];
  const nextZone = layerZones[zoneKeys[zoneIndex + 1]];
  const prevZone = layerZones[zoneKeys[zoneIndex - 1]];

  const [zoneName, setZoneName] = useState(zone.name);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    if (selectedZone.key) {
      setZoneName(zone.name);
    }
  }, [selectedZone.key]);

  const onNameChange = (e) => setZoneName(e.target.value);
  const onNameUpdate = (e) =>
    updateZone(selectedZone.key, selectedZone.parent, { name: e.target.value });
  const onActiveStatusChange = (e, v) => {
    updateZone(selectedZone.key, selectedZone.parent, { active: v });
  };

  const onLayerChange = (e) =>
    updateZoneLayer({
      key: selectedZone.key,
      oldParent: selectedZone.parent,
      newParent: e.target.value,
    });

  const onEditModeChange = (e, v) => updateSelectedZone({ editMode: v });

  const onUploadClick = async () => {
    setUploadLoading(true);
    try {
      await uploadZone({
        key: zone.key,
        parent: selectedZone.parent,
        marketUrl: activeFleet.market,
        fleetUrl: activeFleet.url,
        created: zone.created,
      });
    } catch (err) {
      console.error(err);
    }

    setUploadLoading(false);
  };

  const onDeleteClick = () =>
    openDeleteDialog(() => async () => {
      setDeleteLoading(true);
      try {
        deleteZone({ key: selectedZone.key, parent: selectedZone.parent });
      } catch (_) {
        setDeleteLoading(false);
      }
    });

  const onNextZoneClick = () => selectZone({ key: nextZone.key, parent: selectedZone.parent });
  const onPrevZoneClick = () => selectZone({ key: prevZone.key, parent: selectedZone.parent });

  const showScheduleHandler = () => {
    setShowTable(!showTable);
  };

  return (
    <FloatingPanel position={GeofencesPanelPositions.TOP}>
      <Grid item container>
        <FilledTextField
          value={zoneName}
          type="text"
          required
          label={t("Fleet.Geofences.ZoneNameLabel")}
          className={classes.nameInput}
          onChange={onNameChange}
          onBlur={onNameUpdate}
          underlineColor="white"
        />
        <FilledSelect
          label={t("Fleet.Geofences.ZoneLayerLabel")}
          value={layer.key}
          onChange={onLayerChange}
          FormControlProps={{
            style: {
              maxWidth: "200px",
              marginRight: `${theme.spacing(2)}px`,
              borderRadius: `${theme.spacing(0.5)}px ${theme.spacing(0.5)}px 0 0`,
              overflow: "hidden",
            },
          }}
        >
          {Object.values(layers).map((lr) => (
            <MenuItem key={lr.key} value={lr.key}>
              <LayerWithType type={lr.type}>
                <span className={classes.overflowText}>
                  {lr.name || t(`Fleet.Geofences.LayerTypes.${lr.type}`)}
                  {lr.speed ? `, ${lr.speed} km/h` : ""}
                  {lr.incentive?.amount
                    ? `, ${parseInt(lr.incentive.amount, 10) / 100} ${
                        lr.incentive.currency || billing.currency
                      }`
                    : ""}
                </span>
              </LayerWithType>
            </MenuItem>
          ))}
        </FilledSelect>
        <FormControlLabel
          control={<Checkbox checked={zone.active} onChange={onActiveStatusChange} />}
          label={t("Fleet.Geofences.ZoneActiveLabel")}
          className={classes.switchInput}
        />
        {!sidewalksMode && (
          <PermissionedDisplay permission={Permissions.feature.scheduleGeofenceRules}>
            <Button
              onClick={showScheduleHandler}
              style={{ paddingRight: theme.spacing(4) }}
              startIcon={<DateRangeIcon style={{ fill: zone.id && "red" }} />}
              className={classes.scheduleText}
              disabled={!zone.id}
            >
              {t("Fleet.Geofences.Schedule")}
            </Button>
          </PermissionedDisplay>
        )}
        <div className={classes.rightSideElement}>
          <Button
            onClick={onUploadClick}
            color="secondary"
            disabled={
              uploadLoading ||
              zone.persisted ||
              !Object.keys(zone.hexes).length ||
              layer.type === GeofencesLayerTypes.none ||
              (GeofencesIncentiveLayers.includes(layer.type) &&
                (!layer?.incentive?.amount ||
                  layer.incentive.amount < GeofencesIncentiveLayers.min))
            }
          >
            {uploadLoading ? (
              <CircularProgress size={24} color="secondary" />
            ) : (
              <Grid item container>
                <UploadIcon className={classes.buttonIcon} />
                {t("Fleet.Geofences.Buttons.Upload")}
              </Grid>
            )}
          </Button>
          <Button
            onClick={onDeleteClick}
            disabled={deleteLoading || (!!editMode && editMode !== GeofencesEditModes.hex)}
          >
            {deleteLoading ? (
              <CircularProgress size={24} color="secondary" />
            ) : (
              <Grid item container>
                <DeleteIcon className={classes.buttonIcon} />
                {t("Fleet.Geofences.Buttons.Delete")}
              </Grid>
            )}
          </Button>
        </div>
      </Grid>
      {!sidewalksMode && (
        <Grid item container alignItems="center" className={classes.panelLine}>
          <ToggleButtonGroup
            value={editMode}
            size="small"
            exclusive
            onChange={onEditModeChange}
            className={classes.toggleButtonGroup}
          >
            <ToggleButton
              value={GeofencesEditModes.polygonEdit}
              className={clsx(!zone.polygon && classes.disabledButton)}
              disabled={!zone.polygon}
            >
              <img src={buildAssetsUrl("geofences/polygon-icon.svg")} alt="polygon" />
            </ToggleButton>
            <ToggleButton value={GeofencesEditModes.hex}>
              <img src={buildAssetsUrl("geofences/hex-icon.svg")} alt="hex" />
            </ToggleButton>
          </ToggleButtonGroup>
          <HexFillOptions style={{ flexGrow: 1 }} />
        </Grid>
      )}

      {showTable && (
        <Grid className={classes.panelTopBorder}>
          <ScheduleDisplay />
        </Grid>
      )}

      <Grid
        item
        container
        alignItems="center"
        className={clsx(!showTable && classes.panelTopBorder)}
      >
        <Grid className={classes.rightSideElement}>
          <Button onClick={onPrevZoneClick} disabled={!prevZone}>
            {t("Fleet.Geofences.Buttons.PrevZone")}
          </Button>
          <span className={classes.zoneIndex}>
            {zoneIndex + 1} / {zoneKeys.length}
          </span>
          <Button onClick={onNextZoneClick} disabled={!nextZone}>
            {t("Fleet.Geofences.Buttons.NextZone")}
          </Button>
        </Grid>
      </Grid>

      {deleteDialog}
    </FloatingPanel>
  );
};

EditZonePanel.propTypes = {
  sidewalksMode: PropTypes.bool,
};

EditZonePanel.defaultProps = {
  sidewalksMode: false,
};

export default EditZonePanel;
